import React, { createContext, useContext, useState } from 'react';

interface ContactFormContextType {
  isOpen: boolean;
  selectedServices: string[];
  setSelectedServices: (_services: string[]) => void;
  openContactForm: (_services?: string[]) => void;
  closeContactForm: () => void;
}

const ContactFormContext = createContext<ContactFormContextType | undefined>(
  undefined
);

export const ContactFormProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedServices, setSelectedServicesState] = useState<string[]>([]);

  const setSelectedServices = React.useCallback((services: string[]) => {
    setSelectedServicesState(services);
  }, []);

  const openContactForm = React.useCallback((services?: string[]) => {
    if (services && services.length > 0) {
      setSelectedServicesState(services);
    } else {
      setSelectedServicesState([]);
    }
    setIsOpen(true);
  }, []);

  const closeContactForm = React.useCallback(() => {
    setIsOpen(false);
    // Reset selected services when closing
    setSelectedServicesState([]);
  }, []);

  const contextValue = React.useMemo(
    () => ({
      isOpen,
      selectedServices,
      setSelectedServices,
      openContactForm,
      closeContactForm,
    }),
    [
      isOpen,
      selectedServices,
      setSelectedServices,
      openContactForm,
      closeContactForm,
    ]
  );

  return (
    <ContactFormContext.Provider value={contextValue}>
      {children}
    </ContactFormContext.Provider>
  );
};

export const useContactForm = (): ContactFormContextType => {
  const context = useContext(ContactFormContext);
  if (context === undefined) {
    throw new Error('useContactForm must be used within a ContactFormProvider');
  }
  return context;
};
